import { forwardRef } from 'react';
import cx from 'classnames';
import { useHighlightableText } from '../../../hooks/useHighlightableText';
import { useTruncationTooltip } from '../../../hooks/useTruncationTooltip';
import { useIsRTL } from '../../../locale';
import { useGetDirection } from '../../../locale/useBidiFormatter';
import { renderDesignTokens, useDesignTokens } from '../../../theme';
import { setRef } from '../../../util/ref-util';
import cn from '../Text.css.json';
import '../Text.css';
import { jsx as _jsx } from "react/jsx-runtime";
export const BaseText = /*#__PURE__*/forwardRef((props, ref) => {
  // destruct all props that not HTMLAttributes<HTMLSpanElement> except classname and styles
  const {
    testId,
    dir,
    className,
    style,
    theme,
    wrapText,
    preventTruncationTooltip,
    focusable,
    disabled,
    variant,
    tokens: tokensOverride,
    ariaLabel,
    maxLines,
    children,
    highlightableConfig,
    ...spanProps
  } = props;
  const dt = useDesignTokens();
  const tokens = {
    ...getTextDesignTokens(dt, {
      disabled,
      variant,
      tokens: tokensOverride
    }),
    ...theme?.(dt)
  };
  const setTruncationRef = useTruncationTooltip({
    tooltip: props.title
  });
  const text = useHighlightableText({
    text: typeof children === 'string' ? children : '',
    // children may be a ReactNodeArray or other types or undefined in some cases although it's declared as string.
    // Text only deals with the bidi and highlight for string type children for safety
    className: cn.highlight,
    ...highlightableConfig
  });
  const direction = dir ?? useGetDirection(children);
  const isRTL = useIsRTL();
  return /*#__PURE__*/_jsx("span", {
    "aria-label": ariaLabel,
    dir: direction,
    tabIndex: focusable ? 0 : undefined,
    ...spanProps,
    "aria-disabled": disabled,
    "data-testid": testId,
    className: cx(cn.text, className,
    // the direction set to the 'dir' attribute maybe different from the locale due to BIDI characters. See useBidiFormatter for details
    // dir attribute will affect text alignment as well so set it explicitly to be consistent with locale (RTL or LTR)
    isRTL ? cn.rightAlign : cn.leftAlign, wrapText === false ? cn.nowrap : maxLines !== undefined && cn.hasMaxLines),
    style: {
      ...tokens,
      WebkitLineClamp: wrapText !== false ? maxLines : undefined,
      ...style
    },
    ref: element => {
      if ((wrapText === false || maxLines !== undefined) && !preventTruncationTooltip) {
        setTruncationRef(element);
      }
      setRef(ref, element);
    },
    children: typeof children === 'string' ? text : children
  });
});
export const getTextDesignTokens = (dt, opts) => {
  const textTokens = {
    fg: opts?.disabled ? dt.tokens['color-action-text-standard-disabled'] : dt.tokens['color-body-text'],
    'font-size-m': dt.tokens['font-size-body-text-m'],
    'font-weight': dt.tokens['font-style-weight-400'],
    'highlighted-bg': dt.tokens['color-highlight-text'],
    'highlighted-fg': dt.tokens['color-highlight-text-inverse'],
    'focus-outline': 'var(--focus-light-bg)',
    'focus-outline-offset': 'calc(-1 * var(--focus-width))'
  };
  if (dt.themeVariant === 'light' && opts?.variant === 'primary-light') {
    textTokens.fg = opts?.disabled ? dt.tokens['color-action-text-standard-disabled'] : dt.tokens['color-text-primary-inverse'];
  }
  return {
    ...renderDesignTokens({
      ...textTokens,
      ...(opts?.tokens && opts.tokens?.({
        disabled: opts?.disabled
      }))
    }, 'text'),
    ...(!dt.enableNovaTokens && getTextLegacyDesignTokens(dt, opts))
  };
};
const getTextLegacyDesignTokens = (dt, opts) => {
  const textTokens = {
    fg: opts?.disabled ? dt.reportTokens.stateStandard.disabled.fg : dt.themeVariant === 'light' ? dt.c.n_3 : dt.c.white,
    'font-size-m': dt.reportTokens.font.size.m,
    'font-weight': dt.reportTokens.font.weight.normal,
    'highlighted-bg': dt.reportTokens.text.highlighted.bg,
    'highlighted-fg': dt.reportTokens.text.highlighted.fg
  };
  if (dt.themeVariant === 'light' && opts?.variant === 'primary-light') {
    textTokens.fg = opts?.disabled ? dt.reportTokens.statePrimary.disabled.fg : dt.c.white;
  }
  return {
    ...renderDesignTokens({
      ...textTokens,
      ...(opts?.tokens && opts.tokens?.({
        disabled: opts?.disabled
      }))
    }, 'text')
  };
};
//# sourceMappingURL=BaseText.js.map